/**
 * Transcript Detail Page
 * =======================
 * Detailed view of a meeting transcript
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardBody, Button, Badge, Modal } from '../../components/ui';
import { DashboardLayout } from '../../components/layout';
import transcriptAPI from '../../api/transcripts';
import { useToast } from '../../hooks';

const TranscriptDetailPage = () => {
  const { leadId, meetingId } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [transcript, setTranscript] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFullTranscript, setShowFullTranscript] = useState(false);
  const [notes, setNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);

  // Meeting notes modal state
  const [showMeetingNotesModal, setShowMeetingNotesModal] = useState(false);
  const [meetingNotes, setMeetingNotes] = useState('');
  const [savingMeetingNotes, setSavingMeetingNotes] = useState(false);

  useEffect(() => {
    const fetchTranscript = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await transcriptAPI.getMeetingTranscript(leadId, meetingId);
        if (response.data) {
          setTranscript(response.data);
          setNotes(response.data.notes || '');
          setMeetingNotes(response.data.meeting_notes || '');
        }
      } catch (err) {
        console.error('Error fetching transcript:', err);
        setError(err.response?.data?.detail || 'Failed to load transcript');
        showToast('Failed to load transcript', 'error');
      } finally {
        setLoading(false);
      }
    };

    if (leadId && meetingId) {
      fetchTranscript();
    }
  }, [leadId, meetingId, showToast]);

  const handleBack = () => {
    navigate(`/leads/${leadId}/meetings/${meetingId}`);
  };

  const handleSaveNotes = async () => {
    setSavingNotes(true);
    try {
      await transcriptAPI.updateTranscriptNotes(leadId, meetingId, notes);
      showToast('Notes saved successfully', 'success');
    } catch (err) {
      console.error('Error saving notes:', err);
      showToast('Failed to save notes', 'error');
    } finally {
      setSavingNotes(false);
    }
  };

  const handleOpenMeetingNotesModal = () => {
    setShowMeetingNotesModal(true);
  };

  const handleCloseMeetingNotesModal = () => {
    setShowMeetingNotesModal(false);
    // Reset to original value if user cancels
    setMeetingNotes(transcript?.meeting_notes || '');
  };

  const handleSaveMeetingNotes = async () => {
    setSavingMeetingNotes(true);
    try {
      const response = await transcriptAPI.updateMeetingNotes(leadId, meetingId, meetingNotes);
      if (response.data) {
        // Update the transcript state with the new notes
        setTranscript(prev => ({
          ...prev,
          meeting_notes: response.data.notes
        }));
        showToast('Meeting notes saved successfully', 'success');
        setShowMeetingNotesModal(false);
      }
    } catch (err) {
      console.error('Error saving meeting notes:', err);
      showToast('Failed to save meeting notes', 'error');
    } finally {
      setSavingMeetingNotes(false);
    }
  };

  const formatTimestamp = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="max-w-5xl mx-auto">
          <Card>
            <CardBody>
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading transcript...</p>
              </div>
            </CardBody>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  if (error || !transcript) {
    return (
      <DashboardLayout>
        <div className="max-w-5xl mx-auto">
          <Card>
            <CardBody>
              <div className="text-center py-12">
                <svg
                  className="mx-auto h-12 w-12 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <h3 className="mt-2 text-lg font-medium text-gray-900">
                  {error || 'Transcript not found'}
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  The transcript may not be available yet or the meeting hasn't been recorded.
                </p>
                <div className="mt-6">
                  <Button onClick={handleBack}>← Back to Meeting</Button>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  const segments = transcript.transcript?.segments || [];
  const fullText = transcript.transcript?.full_text || '';

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto">
        {/* Page Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-[28px] font-sans font-bold text-gray-900 dark:text-white mb-2">
              Meeting Transcript
            </h1>
            <p className="text-gray-500 dark:text-gray-400">
              {transcript.meeting_title || 'Meeting Transcript'}
            </p>
            {transcript.tldv_meeting_url && (
              <a
                href={transcript.tldv_meeting_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-primary hover:underline"
              >
                View in tl;dv ↗
              </a>
            )}
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => navigate('/transcripts')}>
              All Transcripts
            </Button>
            <Button variant="outline" onClick={handleBack}>
              ← Back to Meeting
            </Button>
          </div>
        </div>

        {/* Meeting Info Card */}
        <Card className="mb-6">
          <CardBody>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-gray-500">Lead</p>
                <p className="font-medium text-gray-900">
                  {transcript.lead_name || 'Unknown'}
                </p>
                {transcript.company && (
                  <p className="text-sm text-gray-500">{transcript.company}</p>
                )}
              </div>
              <div>
                <p className="text-sm text-gray-500">Date</p>
                <p className="font-medium text-gray-900">
                  {formatDate(transcript.meeting_date)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Duration</p>
                <p className="font-medium text-gray-900">
                  {transcript.duration_minutes ? `${transcript.duration_minutes} minutes` : 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Status</p>
                <Badge variant={transcript.summary ? 'success' : 'warning'}>
                  {transcript.summary ? 'Complete' : 'Transcript Only'}
                </Badge>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Summary Section */}
        {transcript.summary && (
          <Card className="mb-6">
            <CardHeader>
              <h3 className="text-xl font-sans font-semibold text-gray-900 dark:text-white">
                Summary
              </h3>
            </CardHeader>
            <CardBody>
              <p className="text-gray-700 whitespace-pre-wrap">{transcript.summary}</p>
            </CardBody>
          </Card>
        )}

        {/* Action Items Section */}
        {transcript.action_items && transcript.action_items.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <h3 className="text-xl font-sans font-semibold text-gray-900 dark:text-white">
                Action Items
              </h3>
            </CardHeader>
            <CardBody>
              <ul className="space-y-2">
                {transcript.action_items.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center bg-primary/10 text-primary rounded-full text-sm font-medium">
                      {index + 1}
                    </span>
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </CardBody>
          </Card>
        )}

        {/* Key Points Section */}
        {transcript.key_points && transcript.key_points.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <h3 className="text-xl font-sans font-semibold text-gray-900 dark:text-white">
                Key Points
              </h3>
            </CardHeader>
            <CardBody>
              <ul className="space-y-2">
                {transcript.key_points.map((point, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <svg
                      className="flex-shrink-0 w-5 h-5 text-primary mt-0.5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="text-gray-700">{point}</span>
                  </li>
                ))}
              </ul>
            </CardBody>
          </Card>
        )}

        {/* Meeting Notes Section */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-sans font-semibold text-gray-900 dark:text-white">
                Meeting Notes
              </h3>
              <Button
                variant="outline"
                size="sm"
                onClick={handleOpenMeetingNotesModal}
              >
                {transcript.meeting_notes ? 'Edit Notes' : 'Add Notes'}
              </Button>
            </div>
          </CardHeader>
          <CardBody>
            {transcript.meeting_notes ? (
              <div className="text-gray-700 whitespace-pre-wrap bg-gray-50 p-4 rounded-lg">
                {transcript.meeting_notes}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">
                No meeting notes added yet. Click "Add Notes" to add your notes about this meeting.
              </p>
            )}
          </CardBody>
        </Card>

        {/* Notes Section */}
        <Card className="mb-6">
          <CardHeader>
            <h3 className="text-xl font-sans font-semibold text-gray-900 dark:text-white">
              Notes
            </h3>
          </CardHeader>
          <CardBody>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add your notes about this meeting..."
              className="w-full h-32 px-4 py-3 border border-gray-300 rounded-lg focus:ring-0 focus:border-primary resize-none"
            />
            <div className="mt-3 flex justify-end">
              <Button
                onClick={handleSaveNotes}
                disabled={savingNotes}
              >
                {savingNotes ? 'Saving...' : 'Save Notes'}
              </Button>
            </div>
          </CardBody>
        </Card>

        {/* Full Transcript Section */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-sans font-semibold text-gray-900 dark:text-white">
                Full Transcript
              </h3>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFullTranscript(!showFullTranscript)}
              >
                {showFullTranscript ? 'Collapse' : 'Expand'}
              </Button>
            </div>
          </CardHeader>
          <CardBody>
            {showFullTranscript ? (
              <div className="space-y-4 max-h-[600px] overflow-y-auto">
                {segments.length > 0 ? (
                  segments.map((segment, index) => (
                    <div key={index} className="flex gap-4">
                      <span className="flex-shrink-0 text-sm text-gray-400 font-mono w-12">
                        {formatTimestamp(segment.start_time)}
                      </span>
                      <div>
                        <span className="font-medium text-primary">
                          {segment.speaker}:
                        </span>
                        <span className="text-gray-700 ml-2">{segment.text}</span>
                      </div>
                    </div>
                  ))
                ) : fullText ? (
                  <p className="text-gray-700 whitespace-pre-wrap">{fullText}</p>
                ) : (
                  <p className="text-gray-500 text-center py-4">
                    No transcript content available
                  </p>
                )}
              </div>
            ) : (
              <div className="text-center py-4">
                <p className="text-gray-500">
                  {segments.length > 0
                    ? `${segments.length} transcript segments available`
                    : 'Transcript content available'}
                </p>
                <Button
                  variant="link"
                  className="mt-2"
                  onClick={() => setShowFullTranscript(true)}
                >
                  Click to view full transcript
                </Button>
              </div>
            )}
          </CardBody>
        </Card>

        {/* Meeting Notes Modal */}
        <Modal
          isOpen={showMeetingNotesModal}
          onClose={handleCloseMeetingNotesModal}
          title="Meeting Notes"
          size="lg"
          closeOnOverlayClick={!savingMeetingNotes}
          showCloseButton={!savingMeetingNotes}
          footer={
            <>
              <Button
                variant="outline"
                onClick={handleCloseMeetingNotesModal}
                disabled={savingMeetingNotes}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveMeetingNotes}
                disabled={savingMeetingNotes}
              >
                {savingMeetingNotes ? 'Saving...' : 'Save Notes'}
              </Button>
            </>
          }
        >
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Add your notes about this meeting
            </label>
            <textarea
              value={meetingNotes}
              onChange={(e) => setMeetingNotes(e.target.value)}
              placeholder="Enter your meeting notes here..."
              className="w-full h-64 px-4 py-3 border border-gray-300 rounded-lg focus:ring-0 focus:border-primary resize-none"
              disabled={savingMeetingNotes}
            />
            <p className="mt-2 text-sm text-gray-500">
              These notes will be saved to the database and synced with the meeting transcript.
            </p>
          </div>
        </Modal>
      </div>
    </DashboardLayout>
  );
};

export default TranscriptDetailPage;
